import {DateTime} from 'luxon';

import {pluralAmount} from './Locale';

const DATE_FORMAT = 'LLL d, yy';

const FULL_DATE_FORMAT = 'dd, LLLL yyyy';

const DATETIME_FORMAT = 'LLL d, yy hh:mm:ss';

export type DateValue = string | Date | DateTime;

export type DateFormatUnit = 'date' | 'full' | 'datetime';

type DateFormatProps = {
  format?: DateFormatUnit;
};

export const toDateTime = (date: DateValue) => {
  if (typeof date === 'string') {
    date = DateTime.fromISO(date);
  }

  if (date instanceof Date) {
    date = DateTime.fromJSDate(date);
  }

  return date;
};

export const formatDate = (date: DateValue, options?: DateFormatProps) => {
  const {format = 'date'} = options || {};

  date = toDateTime(date);

  switch (format) {
    case 'date': {
      return date.toFormat(DATE_FORMAT);
    }
    case 'full': {
      return date.toFormat(FULL_DATE_FORMAT);
    }
    case 'datetime': {
      return date.toFormat(DATETIME_FORMAT);
    }
  }
};

export const formatRecentDate = (date: DateValue) => {
  date = toDateTime(date);

  const {seconds, minutes, hours, days, months, years} = date.diffNow([
    'seconds',
    'minutes',
    'hours',
    'days',
    'months',
    'years',
  ]);

  const rtf = new Intl.RelativeTimeFormat(getLocale(), {
    numeric: 'auto',
    style: 'long',
  });

  const normalize = date < DateTime.now() ? Math.floor : Math.ceil;

  if (Math.abs(years)) {
    return rtf.format(years, 'years');
  }

  if (Math.abs(months)) {
    return rtf.format(normalize(months), 'months');
  }

  if (Math.abs(days)) {
    return rtf.format(normalize(days), 'days');
  }

  if (Math.abs(hours)) {
    return rtf.format(normalize(hours), 'hours');
  }

  if (Math.abs(minutes)) {
    return rtf.format(normalize(minutes), 'minutes');
  }

  return rtf.format(normalize(seconds), 'seconds');
};

export const formatNonRelativeRecentDate = (date: DateValue) => {
  date = toDateTime(date);

  const {seconds, minutes, hours, days, months, years} = date.diffNow([
    'seconds',
    'minutes',
    'hours',
    'days',
    'months',
    'years',
  ]);

  const normalize = date < DateTime.now() ? Math.floor : Math.ceil;

  if (Math.abs(years)) {
    return pluralAmount('year', 'years', years);
  }

  if (Math.abs(months)) {
    return pluralAmount('month', 'months', months);
  }

  if (Math.abs(days)) {
    return pluralAmount('day', 'days', days);
  }

  if (Math.abs(hours)) {
    return pluralAmount('hour', 'hours', hours);
  }

  if (Math.abs(minutes)) {
    return pluralAmount('minute', 'minutes', minutes);
  }

  return pluralAmount('second', 'seconds', normalize(seconds));
};

const getLocale = () => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  return navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;
};

/*
console.log('5 seconds', formatRecentDate(DateTime.now().minus({seconds: 5})));
console.log('2 months', formatRecentDate(DateTime.now().minus({month: 2})));
console.log('3 days', formatRecentDate(DateTime.now().minus({day: 3})));
console.log('2 years', formatRecentDate(DateTime.now().minus({year: 2})));

console.log('5 seconds', formatRecentDate(DateTime.now().plus({seconds: 5})));
console.log('2 months', formatRecentDate(DateTime.now().plus({month: 2})));
console.log('3 days', formatRecentDate(DateTime.now().plus({day: 3})));
console.log('2 years', formatRecentDate(DateTime.now().plus({year: 2})));
*/
